import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'egac_club_dev_plan_draft';
const THEMES = [
  'Governance',
  'Leadership',
  'Financial Management',
  'Welfare',
  'Places (Facilities)',
  'Athlete & Runner Offer',
  'Performance & Pathways',
  'Volunteers',
  'Marketing & Communications',
  'Sustainability & Wider Impact',
];

function getDefaultState() {
  return {
    clubInfo: {
      clubName: 'East Grinstead Athletics Club',
      publishedDate: '',
      actionReviewDate: '',
      planReviewDate: '',
      writtenBy: '',
    },
    strategic: {
      vision: '',
      values: '',
      goals: '',
    },
    situation: {
      currentSituation: '',
      recentHistory: '',
    },
    swot: {
      strengths: '',
      weaknesses: '',
      opportunities: '',
      threats: '',
    },
    themes: Object.fromEntries(THEMES.map((t) => [t, []])),
  };
}

export function useClubPlan() {
  const [state, setState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Failed to load plan from localStorage:', err);
    }
    return getDefaultState();
  });

  // Auto-save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      console.error('Failed to save plan to localStorage:', err);
    }
  }, [state]);

  const updateClubInfo = useCallback((updates) => {
    setState((prev) => ({
      ...prev,
      clubInfo: {
        ...prev.clubInfo,
        ...updates,
      },
    }));
  }, []);

  const updateVision = useCallback((vision) => {
    setState((prev) => ({
      ...prev,
      strategic: {
        ...prev.strategic,
        vision,
      },
    }));
  }, []);

  const updateValues = useCallback((values) => {
    setState((prev) => ({
      ...prev,
      strategic: {
        ...prev.strategic,
        values,
      },
    }));
  }, []);

  const updateGoals = useCallback((goals) => {
    setState((prev) => ({
      ...prev,
      strategic: {
        ...prev.strategic,
        goals,
      },
    }));
  }, []);

  const updateSituation = useCallback((updates) => {
    setState((prev) => ({
      ...prev,
      situation: {
        ...prev.situation,
        ...updates,
      },
    }));
  }, []);

  const updateSWOT = useCallback((swot) => {
    setState((prev) => ({
      ...prev,
      swot: {
        ...prev.swot,
        ...swot,
      },
    }));
  }, []);

  const updateThemes = useCallback((themeName, objectives) => {
    setState((prev) => ({
      ...prev,
      themes: {
        ...prev.themes,
        [themeName]: objectives,
      },
    }));
  }, []);

  const addObjective = useCallback((themeName, objective) => {
    if (!THEMES.includes(themeName)) {
      console.error(`Unknown theme: ${themeName}`);
      return;
    }

    setState((prev) => ({
      ...prev,
      themes: {
        ...prev.themes,
        [themeName]: [
          ...prev.themes[themeName],
          {
            id: Date.now().toString(),
            ...objective,
          },
        ],
      },
    }));
  }, []);

  const updateObjective = useCallback((themeName, objectiveId, updatedObjective) => {
    if (!THEMES.includes(themeName)) {
      console.error(`Unknown theme: ${themeName}`);
      return;
    }

    setState((prev) => ({
      ...prev,
      themes: {
        ...prev.themes,
        [themeName]: prev.themes[themeName].map((obj) =>
          obj.id === objectiveId ? { id: objectiveId, ...updatedObjective } : obj
        ),
      },
    }));
  }, []);

  const deleteObjective = useCallback((themeName, objectiveId) => {
    if (!THEMES.includes(themeName)) {
      console.error(`Unknown theme: ${themeName}`);
      return;
    }

    setState((prev) => ({
      ...prev,
      themes: {
        ...prev.themes,
        [themeName]: prev.themes[themeName].filter((obj) => obj.id !== objectiveId),
      },
    }));
  }, []);

  const getExportData = useCallback(() => {
    return JSON.stringify(state, null, 2);
  }, [state]);

  const importFromJSON = useCallback((jsonString) => {
    try {
      const imported = JSON.parse(jsonString);
      // Validate structure
      if (imported.clubInfo && imported.strategic && imported.swot && imported.themes) {
        setState(imported);
        return true;
      } else {
        console.error('Invalid plan structure in import data');
        return false;
      }
    } catch (err) {
      console.error('Failed to import plan:', err);
      return false;
    }
  }, []);

  const resetPlan = useCallback(() => {
    setState(getDefaultState());
  }, []);

  const actions = {
    updateClubInfo,
    updateVision,
    updateValues,
    updateGoals,
    updateSituation,
    updateSWOT,
    updateThemes,
    addObjective,
    updateObjective,
    deleteObjective,
    getExportData,
    importFromJSON,
    resetPlan,
  };

  return {
    state,
    actions,
  };
}
